import { LoadingButton } from "@mui/lab";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { CircleX, Trash } from "lucide-react";
import React from "react";
import useLocales from "src/hooks/useLocales";
import { getPluralName } from "src/utils/stringUtils";

interface IConfirmDialogProps {
  open: boolean;
  title: string;
  content: string;
  warning?: string;
  contentParams?: Record<string, any>;
  entityName?: string;
  entityCount?: number;
  confirmText?: string;
  cancelText?: string;
  onClose: VoidFunction;
  onConfirm: VoidFunction;
  confirmLoading?: boolean;
  isManualFormComponent?: boolean;
  hideIcon?: boolean;
  delteConfirmation?: boolean;
  width?: "xs" | "sm" | "md" | "lg" | "xl";
}

const ConfirmDialog: React.FC<IConfirmDialogProps> = ({
  open,
  title = "delete",
  content,
  warning,
  contentParams,
  entityName,
  entityCount,
  confirmText,
  cancelText,
  onClose,
  onConfirm,
  confirmLoading = false,
  isManualFormComponent = false,
  hideIcon = false,
  delteConfirmation = true,
  width,
}) => {
  const { translate } = useLocales();
  const theme = useTheme();

  const getConfirmText = () => {
    if (confirmText) return translate(confirmText);
    if (isManualFormComponent) return translate("delete");
    return translate("yes");
  };

  const getCancelText = () => {
    if (cancelText) return translate(cancelText);
    if (isManualFormComponent) return translate("cancel");
    return translate("no");
  };

  const displayTitle =
    entityName && entityCount && entityCount > 1
      ? `${translate("delete")} ${getPluralName(
          translate(entityName),
          entityCount,
        )}`
      : translate(title);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth={!isManualFormComponent}
      maxWidth={width || (isManualFormComponent ? "sm" : "xs")}
      PaperProps={{
        sx: { width: "100%", maxWidth: "480px", borderRadius: "16px" },
      }}
    >
      <DialogTitle sx={{ px: 2, pt: 2, pb: 1 }}>
        <Stack
          direction="row"
          spacing={1}
          sx={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography variant="h6" sx={{ textTransform: "capitalize" }}>
            {displayTitle}
          </Typography>
          <IconButton onClick={onClose} sx={{ p: 0 }}>
            <CircleX size={22} />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ px: 2, pb: 1 }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {entityName && (
            <Typography variant="body2" color={theme.palette.text.label}>
              {translate("delete_approval")}{" "}
              {getPluralName(translate(entityName), entityCount)}?
            </Typography>
          )}
          {content && (
            <Typography variant="body2" color={theme.palette.text.label}>
              {translate(content, contentParams)}
            </Typography>
          )}
          {warning && (
            <Typography
              variant="body2"
              sx={{ color: "text.primary", fontWeight: "bold" }}
            >
              {translate(warning)}
            </Typography>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose} variant="outlined" size="small">
            {getCancelText()}
          </Button>
          <LoadingButton
            onClick={onConfirm}
            loading={confirmLoading}
            variant="contained"
            size="small"
            sx={
              delteConfirmation
                ? {
                    background: theme.palette.error.dark,
                    "&:hover": {
                      background: theme.palette.error.main,
                    },
                  }
                : {}
            }
            endIcon={
              !hideIcon ? <Trash strokeWidth="1.5px" size="18px" /> : undefined
            }
            loadingPosition={!hideIcon ? "end" : undefined}
          >
            {getConfirmText()}
          </LoadingButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
